import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, AlertTriangle, CheckCircle, HelpCircle } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
import { chatScenarios, ChatOption, ChatResultContent } from '../data/chatScenarios';
import { ChatContainer } from '../components/chat/ChatContainer';
import { ChatBubble } from '../components/chat/ChatBubble';
import { ChatOptions } from '../components/chat/ChatOptions';
import { TypingIndicator } from '../components/chat/TypingIndicator';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { RiskLevelBadge } from '../components/RiskLevelBadge';
import { SEO } from '../components/SEO';
import { RiskLevel } from '../types';
interface Message {
  id: string;
  text: string;
  sender: 'bot' | 'user';
}
export function ChatAssessmentPage() {
  const {
    language,
    t
  } = useLanguage();
  const {
    type
  } = useParams();
  const navigate = useNavigate();
  const scenario = chatScenarios.find(s => s.id === type);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [totalRiskScore, setTotalRiskScore] = useState(0);
  const [maxRiskScore, setMaxRiskScore] = useState(0);
  const [isFinished, setIsFinished] = useState(false);
  const [result, setResult] = useState<{
    level: RiskLevel;
    content: ChatResultContent;
  } | null>(null);
  // Initialize chat
  useEffect(() => {
    if (!scenario) return;
    // Reset state
    setMessages([]);
    setCurrentQuestionIndex(0);
    setTotalRiskScore(0);
    setMaxRiskScore(0);
    setIsFinished(false);
    setResult(null);
    // Start conversation
    setIsTyping(true);
    setTimeout(() => {
      addBotMessage(scenario.intro[language] || scenario.intro['en']);
      setIsTyping(true);
      setTimeout(() => {
        askQuestion(0);
      }, 1000);
    }, 800);
  }, [scenario?.id, language]);
  const addBotMessage = (text: string) => {
    setIsTyping(false);
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      text,
      sender: 'bot'
    }]);
  };
  const addUserMessage = (text: string) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      text,
      sender: 'user'
    }]);
  };
  const askQuestion = (index: number) => {
    if (!scenario) return;
    const question = scenario.questions[index];
    if (!question) {
      finishAssessment();
      return;
    }
    addBotMessage(question.message[language] || question.message['en']);
    setShowOptions(true);
  };
  const handleOptionSelect = (option: ChatOption) => {
    if (!scenario) return;
    setShowOptions(false);
    addUserMessage(option.label[language] || option.label['en']);
    // Update score
    setTotalRiskScore(prev => prev + option.riskScore);
    setMaxRiskScore(prev => prev + 3); // Assuming max score per question is 3
    // Bot feedback
    setIsTyping(true);
    setTimeout(() => {
      addBotMessage(option.feedback[language] || option.feedback['en']);
      // Next question
      setIsTyping(true);
      setTimeout(() => {
        if (currentQuestionIndex + 1 < scenario.questions.length) {
          setCurrentQuestionIndex(prev => prev + 1);
          askQuestion(currentQuestionIndex + 1);
        } else {
          finishAssessment(totalRiskScore + option.riskScore); // Pass updated score
        }
      }, 1500);
    }, 1000);
  };
  const finishAssessment = (finalScore?: number) => {
    if (!scenario) return;
    setIsTyping(false);
    setIsFinished(true);
    const score = finalScore !== undefined ? finalScore : totalRiskScore;
    const max = maxRiskScore || scenario.questions.length * 3;
    const percentage = score / max * 100;
    let level: RiskLevel = 'Low';
    let content = scenario.results.low;
    if (percentage >= 60) {
      level = 'High';
      content = scenario.results.high;
    } else if (percentage >= 30) {
      level = 'Medium';
      content = scenario.results.medium;
    }
    setResult({
      level,
      content
    });
  };
  if (!scenario) {
    return <div className="container mx-auto px-4 py-12 text-center" data-id="element-354">
        <h2 className="text-2xl font-bold mb-4" data-id="element-355">
          {t('chat.scenarioNotFound')}
        </h2>
        <Button href={`/${language}/check-scam`} data-id="element-356">{t('action.goBack')}</Button>
      </div>;
  }
  return <div className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl" data-id="element-357">
      <SEO title={`${scenario.title[language] || scenario.title['en']} - ${t('seo.chat.title')}`} description={t('seo.chat.desc')} data-id="element-358" />

      <div className="mb-6 flex items-center justify-between" data-id="element-359">
        <Link to={`/${language}/check-scam`} className="inline-flex items-center text-sm text-slate-600 hover:text-primary transition-colors" data-id="element-360">
          <ArrowLeft className="w-4 h-4 mr-1" data-id="element-361" />
          {t('protocol.backToHome')}
        </Link>

        {!isFinished && <div className="text-sm font-medium text-slate-500" data-id="element-362">
            {t('chat.question')} {currentQuestionIndex + 1} {t('checkScam.of')}{' '}
            {scenario.questions.length}
          </div>}
      </div>

      <div className="text-center mb-8" data-id="element-363">
        <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 mb-2" data-id="element-364">
          {scenario.title[language] || scenario.title['en']}
        </h1>
        <p className="text-slate-600" data-id="element-365">
          {isFinished ? t('chat.assessmentComplete') : t('chat.assistant')}
        </p>
      </div>

      {/* Chat Area - Centered */}
      <div className="flex flex-col items-center" data-id="element-366">
        <div className={`w-full max-w-2xl ${isFinished ? 'hidden' : ''}`} data-id="element-367">
          <ChatContainer data-id="element-368">
            {messages.map(msg => <ChatBubble key={msg.id} message={msg.text} sender={msg.sender} data-id="element-369" />)}
            {isTyping && <div className="flex justify-start mb-4 animate-fade-in" data-id="element-370">
                <TypingIndicator data-id="element-371" />
              </div>}
          </ChatContainer>

          {showOptions && !isTyping && <ChatOptions options={scenario.questions[currentQuestionIndex].options} onSelect={handleOptionSelect} data-id="element-372" />}
        </div>

        {/* Result Area (Shows when finished) */}
        {isFinished && result && <div className="w-full animate-slide-up" data-id="element-373">
            <Card className={`p-6 sm:p-8 border-t-8 ${result.level === 'High' ? 'border-t-destructive bg-red-50/50' : result.level === 'Medium' ? 'border-t-warning bg-yellow-50/50' : 'border-t-success bg-green-50/50'}`} data-id="element-374">
              <div className="flex flex-col items-center text-center mb-8" data-id="element-375">
                <RiskLevelBadge level={result.level} data-id="element-376" />
                <h2 className="text-3xl font-bold mt-4 mb-2 text-slate-900" data-id="element-377">
                  {result.content.title[language] || result.content.title['en']}
                </h2>
                <p className="text-lg text-slate-700 max-w-2xl" data-id="element-378">
                  {result.content.description[language] || result.content.description['en']}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-379">
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-380">
                  <h3 className="font-bold text-lg mb-4 flex items-center" data-id="element-381">
                    <AlertTriangle className="w-5 h-5 mr-2 text-primary" data-id="element-382" />
                    {t('checkScam.recommendedActions')}
                  </h3>
                  <ul className="space-y-3" data-id="element-383">
                    {result.content.actions.map((action, idx) => <li key={idx} className="flex items-start text-slate-700" data-id="element-384">
                        <span className="mr-2" data-id="element-385">•</span>
                        {action[language] || action['en']}
                      </li>)}
                  </ul>
                </div>

                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-386">
                  <h3 className="font-bold text-lg mb-4 flex items-center" data-id="element-387">
                    <HelpCircle className="w-5 h-5 mr-2 text-primary" data-id="element-388" />
                    {t('chat.nextSteps')}
                  </h3>
                  <div className="space-y-3" data-id="element-389">
                    <Button href={`/${language}/emergency-protocol`} variant="destructive" fullWidth className="justify-center" data-id="element-390">
                      {t('nav.emergency')}
                    </Button>
                    <Button href={`/${language}/where-to-report`} variant="outline" fullWidth className="justify-center" data-id="element-391">
                      {t('nav.report')}
                    </Button>
                    <Button onClick={() => window.location.reload()} variant="ghost" fullWidth className="justify-center" leftIcon={<RefreshCw className="w-4 h-4" data-id="element-393" />} data-id="element-392">
                      {t('chat.restartAssessment')}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>}
      </div>
    </div>;
}